import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronUp,
  Search,
  Shield,
  Sparkles,
  Zap,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { getReleaseNotes, markReleaseNoteSeen } from '@/services/releaseNotesApi';

const PAGE_SIZE = 5;
const EMPTY_FILTERS = { search: '', fromDate: '', toDate: '' };
const iconMap = {
  sparkles: Sparkles,
  'arrow-up': ArrowUp,
  check: Check,
  shield: Shield,
  zap: Zap,
};
const iconStyles = {
  sparkles: 'bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300',
  'arrow-up': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300',
  check: 'bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300',
  shield: 'bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300',
  zap: 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300',
};

const displayDate = (note) => {
  if (note.published_at) {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'long',
      timeZone: 'UTC',
    }).format(new Date(note.published_at));
  }
  return note.date || 'Date unavailable';
};

const ReleaseSection = ({ section }) => {
  const Icon = iconMap[section.icon] || Sparkles;
  return (
    <section className="space-y-2" aria-label={section.title}>
      <h4 className="flex items-center gap-2 text-sm font-semibold text-stone-800 dark:text-stone-100">
        <span className={cn('rounded-md p-1.5', iconStyles[section.icon] || iconStyles.sparkles)}>
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        {section.title}
      </h4>
      <ul className="space-y-2 pl-2 sm:pl-9">
        {section.items.map((item, index) => (
          <li key={`${item.title}-${index}`} className="text-sm text-stone-600 dark:text-stone-300">
            <span className="font-medium text-stone-800 dark:text-stone-100">{item.title}</span>
            {item.description && <span className="block leading-relaxed">{item.description}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
};

const WhatsNewModal = ({
  open,
  onOpenChange,
  onSeen,
  loadReleaseNotes = getReleaseNotes,
  acknowledgeReleaseNote = markReleaseNoteSeen,
}) => {
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [reloadKey, setReloadKey] = useState(0);

  const unfilteredFirstPage = page === 1
    && !filters.search && !filters.fromDate && !filters.toDate;

  const load = useCallback(async (signal) => {
    setLoading(true);
    setError('');
    try {
      const data = await loadReleaseNotes({ page, perPage: PAGE_SIZE, ...filters });
      if (signal.cancelled) return;
      setResult(data);
      setExpanded(data.items.reduce((state, note, index) => ({
        ...state,
        [note.id]: index === 0,
      }), {}));

      // Only a successful, unfiltered newest-note load caused by opening the
      // modal may clear unread state. Filtered/failed history reads never do.
      if (unfilteredFirstPage && data.newest_release_note_id) {
        try {
          await acknowledgeReleaseNote(data.newest_release_note_id);
          if (!signal.cancelled) onSeen();
        } catch {
          // Keep the indicator until persistence succeeds on a future open.
        }
      }
    } catch (requestError) {
      if (!signal.cancelled) setError(requestError.message || 'Unable to load release notes.');
    } finally {
      if (!signal.cancelled) setLoading(false);
    }
  }, [acknowledgeReleaseNote, filters, loadReleaseNotes, onSeen, page, unfilteredFirstPage]);

  useEffect(() => {
    if (!open) return undefined;
    const signal = { cancelled: false };
    load(signal);
    return () => { signal.cancelled = true; };
  }, [load, open, reloadKey]);

  useEffect(() => {
    if (open) return;
    // Every new open starts at the latest unfiltered page so a newly published
    // note can be loaded and acknowledged even if the previous visit ended deep
    // in filtered history.
    setPage(1);
    setDraftFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
  }, [open]);

  const allExpanded = useMemo(() => (
    Boolean(result?.items.length) && result.items.every((note) => expanded[note.id])
  ), [expanded, result]);

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setFilters({
      search: draftFilters.search.trim(),
      fromDate: draftFilters.fromDate,
      toDate: draftFilters.toDate,
    });
  };

  const clearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setPage(1);
    setFilters(EMPTY_FILTERS);
  };

  const build = process.env.REACT_APP_GIT_COMMIT
    ? process.env.REACT_APP_GIT_COMMIT.slice(0, 7)
    : 'local';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92dvh] w-[calc(100%-1rem)] max-w-3xl flex-col gap-0 overflow-hidden border-amber-600/40 p-0 sm:w-full">
        <DialogHeader className="shrink-0 border-b border-stone-200 bg-gradient-to-r from-amber-50 to-white px-5 py-5 pr-12 text-left dark:border-stone-700 dark:from-amber-950/30 dark:to-background">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-5 w-5 text-amber-600" aria-hidden="true" />
            What&apos;s New
          </DialogTitle>
          <DialogDescription>
            Recent features, fixes, and improvements in plain language.
            <span className="ml-2 whitespace-nowrap font-mono text-xs">Build {build}</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={applyFilters} className="shrink-0 space-y-3 border-b border-stone-200 p-4 dark:border-stone-700">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-stone-400" aria-hidden="true" />
            <Input
              value={draftFilters.search}
              onChange={(event) => setDraftFilters((current) => ({ ...current, search: event.target.value }))}
              className="pl-9"
              maxLength={200}
              placeholder="Search release titles and details"
              aria-label="Search release notes"
            />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-end">
            <label className="text-xs font-medium text-stone-600 dark:text-stone-300">
              From
              <Input
                type="date"
                value={draftFilters.fromDate}
                onChange={(event) => setDraftFilters((current) => ({ ...current, fromDate: event.target.value }))}
                max={draftFilters.toDate || undefined}
                className="mt-1"
              />
            </label>
            <label className="text-xs font-medium text-stone-600 dark:text-stone-300">
              Through
              <Input
                type="date"
                value={draftFilters.toDate}
                onChange={(event) => setDraftFilters((current) => ({ ...current, toDate: event.target.value }))}
                min={draftFilters.fromDate || undefined}
                className="mt-1"
              />
            </label>
            <Button type="submit" size="sm">Apply filters</Button>
            <Button type="button" size="sm" variant="ghost" onClick={clearFilters}>Clear</Button>
          </div>
        </form>

        <div className="min-h-0 flex-1 overflow-y-auto p-4" aria-live="polite">
          {loading && (
            <div aria-label="Loading release notes" className="space-y-4">
              {[0, 1, 2].map((key) => <Skeleton key={key} className="h-28 w-full" />)}
            </div>
          )}

          {!loading && error && (
            <div role="alert" className="flex min-h-48 flex-col items-center justify-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6 text-center dark:border-red-900 dark:bg-red-950/30">
              <AlertCircle className="h-7 w-7 text-red-600" aria-hidden="true" />
              <p className="font-medium">Release history is unavailable</p>
              <p className="max-w-md text-sm text-stone-600 dark:text-stone-300">{error}</p>
              <Button type="button" variant="outline" onClick={() => setReloadKey((key) => key + 1)}>Retry</Button>
            </div>
          )}

          {!loading && !error && result?.items.length === 0 && (
            <div className="flex min-h-48 flex-col items-center justify-center gap-2 text-center">
              <Sparkles className="h-8 w-8 text-amber-600" aria-hidden="true" />
              <p className="font-medium">No release notes found</p>
              <p className="text-sm text-stone-500">
                {filters.search || filters.fromDate || filters.toDate
                  ? 'Try clearing or widening your filters.'
                  : 'New weekly updates will appear here after they are published.'}
              </p>
            </div>
          )}

          {!loading && !error && result?.items.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs text-stone-500">
                <span>{result.total} {result.total === 1 ? 'release' : 'releases'}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setExpanded(result.items.reduce((state, note) => ({
                    ...state,
                    [note.id]: !allExpanded,
                  }), {}))}
                >
                  {allExpanded ? 'Collapse all' : 'Expand all'}
                </Button>
              </div>
              {result.items.map((note) => (
                <Collapsible
                  key={note.id}
                  open={Boolean(expanded[note.id])}
                  onOpenChange={(nextOpen) => setExpanded((current) => ({
                    ...current,
                    [note.id]: nextOpen,
                  }))}
                  className="rounded-xl border border-stone-200 bg-white shadow-sm dark:border-stone-700 dark:bg-stone-900/40"
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-auto w-full justify-between rounded-xl px-4 py-3 text-left"
                      aria-label={`${expanded[note.id] ? 'Collapse' : 'Expand'} release from ${displayDate(note)}`}
                    >
                      <span>
                        <span className="block font-semibold">{displayDate(note)}</span>
                        {note.intro && <span className="mt-1 line-clamp-1 block text-xs font-normal text-stone-500">{note.intro}</span>}
                      </span>
                      {expanded[note.id]
                        ? <ChevronUp className="h-4 w-4 shrink-0" aria-hidden="true" />
                        : <ChevronDown className="h-4 w-4 shrink-0" aria-hidden="true" />}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4 border-t border-stone-100 px-4 py-4 dark:border-stone-800">
                    {note.intro && <p className="text-sm leading-relaxed text-stone-600 dark:text-stone-300">{note.intro}</p>}
                    {note.sections.map((section, index) => (
                      <ReleaseSection key={`${section.title}-${index}`} section={section} />
                    ))}
                    {note.outro && <p className="text-sm italic text-stone-500">{note.outro}</p>}
                  </CollapsibleContent>
                </Collapsible>
              ))}
            </div>
          )}
        </div>

        {!loading && !error && result && result.total_pages > 1 && (
          <div className="flex shrink-0 items-center justify-between border-t border-stone-200 px-4 py-3 text-sm dark:border-stone-700">
            <Button type="button" size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</Button>
            <span>Page {page} of {result.total_pages}</span>
            <Button type="button" size="sm" variant="outline" disabled={page >= result.total_pages} onClick={() => setPage((value) => value + 1)}>Next</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WhatsNewModal;
